import React from "react";
import { CheckCircle } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BUDGET_OPTIONS = [
  "Less than R5,000/month",
  "R5,000 - R15,000/month",
  "R15,000 - R30,000/month",
  "R30,000 - R60,000/month",
  "R60,000+/month",
  "Not sure yet",
];

const CHANNEL_OPTIONS = [
  "Social Media (Organic)",
  "Paid Ads (Google/Meta)",
  "Email Marketing",
  "SEO / Content Marketing",
  "Referrals / Word of Mouth",
  "WhatsApp Marketing",
  "Events / Networking",
  "Cold Outreach",
  "PR / Media",
  "None currently",
];

export const MarketingGoalsStep = ({ formData, onChange, toggleChannel }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-xl font-semibold text-white mb-1">Marketing & Goals</h2>
      <p className="text-gray-500 text-sm">Your competitive landscape, goals, and current marketing efforts.</p>
    </div>

    <div>
      <label className="block text-sm text-gray-300 mb-1.5">Who are your top 5 competitors, and what are their websites? *</label>
      <Textarea value={formData.competitors} onChange={(e) => onChange("competitors", e.target.value)} placeholder={"1. Competitor A — www.competitorA.com\n2. Competitor B — www.competitorB.com\n3. Competitor C — www.competitorC.com\n4. Competitor D — www.competitorD.com\n5. Competitor E — www.competitorE.com"} rows={6} className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" data-testid="input-competitors" required />
    </div>

    <div>
      <label className="block text-sm text-gray-300 mb-1.5">What keywords do you currently believe best describe your business or services? *</label>
      <Textarea value={formData.keywords} onChange={(e) => onChange("keywords", e.target.value)} placeholder="e.g. digital marketing, web design, AI consulting, Cape Town plumber" rows={2} className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" data-testid="input-keywords" required />
    </div>

    <div>
      <label className="block text-sm text-gray-300 mb-1.5">Monthly Marketing Budget</label>
      <Select value={formData.marketing_budget} onValueChange={(v) => onChange("marketing_budget", v)}>
        <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-marketing-budget"><SelectValue placeholder="Select budget range" /></SelectTrigger>
        <SelectContent>{BUDGET_OPTIONS.map((opt) => (<SelectItem key={opt} value={opt}>{opt}</SelectItem>))}</SelectContent>
      </Select>
    </div>

    <div>
      <label className="block text-sm text-gray-300 mb-2">Current Marketing Channels</label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="channel-options">
        {CHANNEL_OPTIONS.map((channel) => (
          <button key={channel} type="button" onClick={() => toggleChannel(channel)} className={`text-left px-3 py-2.5 rounded-lg text-sm transition-all border ${formData.current_channels.includes(channel) ? "bg-[#3056D3]/20 border-[#3056D3] text-[#00B5E2]" : "bg-white/5 border-white/10 text-gray-400 hover:border-white/20"}`} data-testid={`channel-${channel.replace(/[^a-zA-Z]/g, "-").toLowerCase()}`}>
            {formData.current_channels.includes(channel) && <CheckCircle className="w-3.5 h-3.5 inline mr-1.5 text-[#00B5E2]" />}
            {channel}
          </button>
        ))}
      </div>
    </div>

    <div>
      <label className="block text-sm text-gray-300 mb-1.5">What are your primary business goals for the next 6 to 12 months? *</label>
      <Textarea value={formData.business_goals} onChange={(e) => onChange("business_goals", e.target.value)} placeholder="e.g. More leads, more sales, brand awareness, expand to new markets..." rows={3} className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" data-testid="input-business-goals" required />
    </div>

    <div>
      <label className="block text-sm text-gray-300 mb-1.5">What are your biggest challenges right now? *</label>
      <Textarea value={formData.biggest_challenges} onChange={(e) => onChange("biggest_challenges", e.target.value)} placeholder="What's currently holding your business back?" rows={3} className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" data-testid="input-biggest-challenges" required />
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm text-gray-300 mb-1.5">Existing Tools / Software</label>
        <Textarea value={formData.existing_tools} onChange={(e) => onChange("existing_tools", e.target.value)} placeholder="CRM, email platform, analytics tools..." rows={2} className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" data-testid="input-existing-tools" />
      </div>
      <div>
        <label className="block text-sm text-gray-300 mb-1.5">Social Media Profiles</label>
        <Textarea value={formData.social_media_profiles} onChange={(e) => onChange("social_media_profiles", e.target.value)} placeholder="Links to Facebook, Instagram, LinkedIn, etc." rows={2} className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" data-testid="input-social-profiles" />
      </div>
    </div>
  </div>
);
